import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  DocumentReference,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { MaintenanceRecord, MaintenanceFilters, MaintenanceEvent, MaintenanceDocument } from '../types/maintenance';
import { notificationService } from './notificationService';
import { organizationService } from './organizationService';
import { vehicleService } from './vehicleService';
import { userService } from './userService';

const MAINTENANCE_COLLECTION = 'maintenanceRecords';

export const createMaintenanceRecord = async (data: Omit<MaintenanceRecord, 'id'>): Promise<string> => {
  try {
    // Ensure all required fields are present and properly typed
    const recordData = {
      ...data,
      // Only set timestamps if they're not already set
      createdAt: data.createdAt || Timestamp.now(),
      updatedAt: data.updatedAt || Timestamp.now(),
      // Ensure arrays are initialized
      events: data.events || [],
      documents: data.documents || [],
      // Ensure closeDate is properly handled
      closeDate: data.status === 'completed' ? (data.closeDate || Timestamp.now()) : undefined,
      // Ensure other required fields have defaults
      costHT: data.costHT || 0,
      costTTC: data.costTTC || 0,
      description: data.description || '',
      openDate: data.openDate || Timestamp.now(),
      // Optional fields
      serviceProviderId: data.serviceProviderId || undefined,
      detailedOperations: data.detailedOperations ? {
        parts: Array.isArray(data.detailedOperations.parts) ? data.detailedOperations.parts : [],
        labor: Array.isArray(data.detailedOperations.labor) ? data.detailedOperations.labor : []
      } : undefined
    };
    
    // Créer l'enregistrement de maintenance
    const docRef = await addDoc(collection(db, MAINTENANCE_COLLECTION), recordData);

    try {
      // Récupérer les informations du véhicule
      const vehicle = await vehicleService.getVehicle(data.vehicleId);
      
      if (vehicle) {
        // Récupérer les admins de l'organisation
        const admins = await organizationService.getOrganizationAdmins(data.organizationId);

        // Envoyer une notification à chaque admin
        for (const admin of admins) {
          // Notification email
          await notificationService.sendNotificationEmail({
            to_email: admin.email,
            to_name: admin.displayName || admin.email,
            subject: `Nouvelle maintenance - ${vehicle.make} ${vehicle.model}`,
            message: notificationService.formatMaintenanceMessage({...data, id: docRef.id}, vehicle)
          });

          // Récupérer l'utilisateur par email pour les préférences
          const user = await userService.findUserByEmail(admin.email);
          if (!user?.id) continue;

          // Notification push si activée
          const userPrefs = await notificationService.getNotificationPreferences(user.id);
          if (userPrefs.enabled && userPrefs.maintenance) {
            await notificationService.showNotification(
              `Nouvelle maintenance - ${vehicle.make} ${vehicle.model}`,
              {
                body: `Type: ${data.type}\nDescription: ${data.description}`,
                icon: '/pwa-192x192.png',
                data: {
                  url: `/admin/maintenance/${docRef.id}`
                }
              }
            );
          }
        }
      }
    } catch (notifError) {
      // Log l'erreur mais ne pas bloquer la création de la maintenance
      console.error('Error sending maintenance notification:', notifError);
    }

    return docRef.id;
  } catch (error) {
    console.error('Error creating maintenance record:', error);
    throw error;
  }
};

export const updateMaintenanceRecord = async (
  id: string,
  data: Partial<MaintenanceRecord>
): Promise<void> => {
  try {
    const docRef = doc(db, MAINTENANCE_COLLECTION, id);
    // Ensure data is properly formatted before update
    const updateData = {
      ...data,
      updatedAt: Timestamp.now(),
      // Ensure closeDate is properly handled
      closeDate: data.status === 'completed' ? (data.closeDate || Timestamp.now()) : undefined,
      // Ensure arrays are initialized if present in update
      ...(data.events && { events: data.events }),
      ...(data.documents && { documents: data.documents }),
      // Handle detailedOperations consistently
      ...(data.detailedOperations && {
        detailedOperations: {
          parts: Array.isArray(data.detailedOperations.parts) ? data.detailedOperations.parts : [],
          labor: Array.isArray(data.detailedOperations.labor) ? data.detailedOperations.labor : []
        }
      })
    };
    await updateDoc(docRef, updateData);
  } catch (error) {
    console.error('Error updating maintenance record:', error);
    throw error;
  }
};

export const deleteMaintenanceRecord = async (id: string): Promise<void> => {
  try {
    const docRef = doc(db, MAINTENANCE_COLLECTION, id);
    await deleteDoc(docRef);
  } catch (error) {
    console.error('Error deleting maintenance record:', error);
    throw error;
  }
};

export const getMaintenanceRecord = async (id: string): Promise<MaintenanceRecord | null> => {
  try {
    const docRef = doc(db, MAINTENANCE_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() } as MaintenanceRecord;
    }
    return null;
  } catch (error) {
    console.error('Error getting maintenance record:', error);
    throw error;
  }
};

export const getMaintenanceRecords = async (
  organizationId: string,
  filters?: MaintenanceFilters
): Promise<MaintenanceRecord[]> => {
  if (!organizationId) {
    throw new Error('Organization ID is required');
  }

  try {
    // Construction de la requête avec les conditions de base
    let queryConditions = [
      where('organizationId', '==', organizationId)
    ];

    // Ajout du filtre véhicule si présent
    if (filters?.vehicleId) {
      queryConditions.push(where('vehicleId', '==', filters.vehicleId));
    }

    // Ajout des filtres de date si présents
    if (filters?.startDate) {
      queryConditions.push(where('openDate', '>=', Timestamp.fromDate(filters.startDate)));
    }
    if (filters?.endDate) {
      // Ajouter un jour à la date de fin pour inclure tout le dernier jour
      const endDate = new Date(filters.endDate);
      endDate.setDate(endDate.getDate() + 1);
      queryConditions.push(where('openDate', '<', Timestamp.fromDate(endDate)));
    }

    // Construction de la requête avec les conditions de base
    const conditions = [
      where('organizationId', '==', organizationId)
    ];

    // Ajout du filtre véhicule
    if (filters?.vehicleId) {
      conditions.push(where('vehicleId', '==', filters.vehicleId));
    }

    // Ajout des filtres de date
    if (filters?.startDate) {
      const startTimestamp = Timestamp.fromDate(new Date(filters.startDate));
      conditions.push(where('openDate', '>=', startTimestamp));
    }

    if (filters?.endDate) {
      const endDate = new Date(filters.endDate);
      endDate.setHours(23, 59, 59, 999); // Fin de la journée
      const endTimestamp = Timestamp.fromDate(endDate);
      conditions.push(where('openDate', '<=', endTimestamp));
    }

    // Construction de la requête finale avec tri
    const baseQuery = query(
      collection(db, MAINTENANCE_COLLECTION),
      ...conditions,
      orderBy('openDate', 'desc')
    );

    const querySnapshot = await getDocs(baseQuery);

    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      description: doc.data().description || '',
      costHT: doc.data().costHT || 0,
      costTTC: doc.data().costTTC || 0,
      events: doc.data().events || [],
      documents: doc.data().documents || [],
      detailedOperations: doc.data().detailedOperations || { parts: [], labor: [] }
    } as MaintenanceRecord));

  } catch (error: any) {
    console.error('Error getting maintenance records:', error);
    if (error.code === 'permission-denied') {
      throw new Error('Insufficient permissions to access maintenance records');
    }
    throw new Error('Failed to fetch maintenance records');
  }
};

export const addMaintenanceEvent = async (
  maintenanceId: string,
  event: Omit<MaintenanceEvent, 'id'>
): Promise<void> => {
  try {
    const maintenanceRef = doc(db, MAINTENANCE_COLLECTION, maintenanceId);
    const maintenanceDoc = await getDoc(maintenanceRef);
    
    if (!maintenanceDoc.exists()) {
      throw new Error('Maintenance record not found');
    }

    const currentEvents = maintenanceDoc.data().events || [];
    const newEvent = {
      ...event,
      id: crypto.randomUUID(),
    };

    await updateDoc(maintenanceRef, {
      events: [...currentEvents, newEvent],
      updatedAt: Timestamp.now(),
    });
  } catch (error) {
    console.error('Error adding maintenance event:', error);
    throw error;
  }
};

export const addMaintenanceDocument = async (
  maintenanceId: string,
  document: Omit<MaintenanceDocument, 'id'>
): Promise<void> => {
  try {
    const maintenanceRef = doc(db, MAINTENANCE_COLLECTION, maintenanceId);
    const maintenanceDoc = await getDoc(maintenanceRef);
    
    if (!maintenanceDoc.exists()) {
      throw new Error('Maintenance record not found');
    }

    const currentDocuments = maintenanceDoc.data().documents || [];
    const newDocument = {
      ...document,
      id: crypto.randomUUID(),
    };

    await updateDoc(maintenanceRef, {
      documents: [...currentDocuments, newDocument],
      updatedAt: Timestamp.now(),
    });
  } catch (error) {
    console.error('Error adding maintenance document:', error);
    throw error;
  }
};
